// The transport seam. Every Tauri command call in the app goes through here, so that serving the same
// UI to a browser (see docs/archive/REMOTE_UI_PLAN.md) is a change in ONE module rather than ~130 call sites.
//
// Today there is exactly one transport — the in-process Tauri IPC — and this file is a pass-through with
// no behavior change. The remote transport (HTTPS to the desktop, default-deny allowlisted server-side)
// slots in at `pickTransport` when Phase 1 lands.
//
// Rule for everyone else: import `invoke` / `assetUrl` / `listen` from HERE, never from
// `@tauri-apps/api/*` directly. The exceptions are deliberately desktop-only modules (window controls,
// updater, dialogs) — see docs/COMMANDS.md for which capabilities never leave the desktop.
import { convertFileSrc, invoke as tauriInvoke } from "@tauri-apps/api/core";
import { listen as tauriListen, type UnlistenFn } from "@tauri-apps/api/event";

/** A Tauri IPC channel, for a command that streams its answer back (lib/nativeFetch.ts). Desktop only. */
export { Channel } from "@tauri-apps/api/core";

/** Arguments accepted by a Tauri command — the shape `invoke` already takes. */
export type CommandArgs = Record<string, unknown>;

/**
 * A transport carries the CONTEXT of a call (which session, which vault); command arguments carry only
 * its subject. That split is deliberate: commands used to take `images_dir` / `blobs_dir` from the caller,
 * which is exactly how a remote client could aim a destructive command at any path. Those now come from
 * Rust state, and session identity will arrive the same way — on the request, never as an argument. See
 * docs/archive/REMOTE_UI_PLAN.md §0.3.
 */
export interface Transport {
  invoke<T>(cmd: string, args?: CommandArgs): Promise<T>;
  /** A URL the webview can load for a stored asset (card art, generated images). */
  assetUrl(key: string, protocol?: string): string;
  listen<T>(event: string, handler: (payload: T) => void): Promise<UnlistenFn>;
}

/**
 * Are we running outside the Tauri webview (i.e. served to a browser)?
 *
 * Keyed off the runtime, not a build flag: ONE bundle serves both the desktop webview and remote
 * clients, so this must be answerable at load time in either.
 */
export function isRemote(): boolean {
  return typeof window !== "undefined" && !("__TAURI_INTERNALS__" in window);
}

const tauri: Transport = {
  invoke: <T,>(cmd: string, args?: CommandArgs) => tauriInvoke<T>(cmd, args),
  assetUrl: (key, protocol) => convertFileSrc(key, protocol),
  listen: <T,>(event: string, handler: (payload: T) => void) =>
    tauriListen<T>(event, (e) => handler(e.payload)),
};

/** Thrown when the desktop refuses a call. `status` lets callers tell "not allowed here" (403) from a
 *  command that simply failed (400), which a bare message cannot. */
/** Fired at `window` when the desktop says this device is no longer authorised. */
export const REMOTE_UNAUTHORIZED = "rc-remote-unauthorized";

/** Fired at `window` when another device holds the write lease. `detail.holder` names them. */
export const REMOTE_BUSY = "rc-remote-busy";
/** A command the desktop will not run for a remote client (403). Dispatched as well as thrown, because
 *  most callers swallow their errors — which is exactly how "this button does nothing on my phone" gets
 *  reported with no clue as to which command was refused. */
export const REMOTE_FORBIDDEN = "rc-remote-forbidden";

export class RemoteRpcError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "RemoteRpcError";
  }
}

/**
 * Talk to the desktop over HTTPS instead of IPC. Exported so its tests drive the REAL implementation
 * rather than a copy of it — a copied contract passes while the shipped code is broken.
 *
 * The device cookie is the credential and the browser sends it automatically (same origin), so there is
 * nothing to attach here. The desktop decides what may run — see `remote_acl::rpc_gate`; this side must
 * never pre-filter, because a client-side check is not a security boundary and would only hide errors.
 */
export const remoteTransport: Transport = {
  async invoke<T>(cmd: string, args?: CommandArgs): Promise<T> {
    const res = await fetch("/api/rpc", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cmd, args: args ?? {} }),
    });
    if (!res.ok) {
      // 401 means the device is no longer authorised (PIN window expired, device revoked, desktop signed
      // out). That is a GATE CHANGE, not a failure: the app listens for this and shows pairing/PIN again
      // rather than letting the error reach the ErrorBoundary as a crash.
      if (res.status === 401) window.dispatchEvent(new CustomEvent(REMOTE_UNAUTHORIZED));
      const detail = (await res.json().catch(() => null)) as { error?: string; holder?: string; code?: string } | null;
      // Another device is writing. Surfaced as an offer to take over rather than a dead end — a device
      // that fell asleep mid-edit must not be able to lock its owner out of their own vault.
      if (res.status === 409) {
        window.dispatchEvent(new CustomEvent(REMOTE_BUSY, { detail: { holder: detail?.holder ?? "Another device" } }));
      }
      const msg = detail?.error ?? `Request failed (${res.status})`;
      // Announced only for a refused COMMAND. `code: "settings_key"` means the command itself is fine and
      // one key is being withheld — that is the design working (secrets stay on the desktop) and callers
      // already cope, so telling the user "settings_get isn't available" was both noise and a lie.
      // Guarded on `window`: this must never replace the error it is announcing, and a caller without one
      // (tests, any non-DOM host) would otherwise get a ReferenceError instead of the RemoteRpcError.
      if (res.status === 403 && detail?.code !== "settings_key" && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(REMOTE_FORBIDDEN, { detail: { cmd, message: msg } }));
      }
      throw new RemoteRpcError(msg, res.status);
    }
    return (await res.json()) as T;
  },
  // Path-addressed, matching the aicc-img protocol, so imgSrc/thumbSrc can append ?v= and ?thumb=1.
  assetUrl: (key) => `/api/img/${encodeURIComponent(key)}`,
  // Every Tauri event the app subscribes to is a DESKTOP-only capability — OS notification clicks,
  // dictation from the Rust recognizer, the AICC browser-link hint, and the remote server's own serving
  // plumbing. None of them can fire for a remote client, so there is nothing to deliver and no event
  // channel to build. If a future event genuinely needs to reach remote clients, this is where SSE goes.
  listen: async () => () => {},
};

/** Resolved once: the environment cannot change mid-session. */
let active: Transport | null = null;
function transport(): Transport {
  if (!active) active = isRemote() ? remoteTransport : tauri;
  return active;
}

/** Call a Tauri command. Same signature as `@tauri-apps/api/core`'s `invoke`. */
export function invoke<T>(cmd: string, args?: CommandArgs): Promise<T> {
  return transport().invoke<T>(cmd, args);
}

/** URL for a stored asset. `protocol` selects the custom scheme (e.g. `aicc-img`). */
export function assetUrl(key: string, protocol?: string): string {
  return transport().assetUrl(key, protocol);
}

/** Subscribe to a backend event. Returns the unsubscribe function. */
export function listen<T>(event: string, handler: (payload: T) => void): Promise<UnlistenFn> {
  return transport().listen<T>(event, handler);
}

/** Swap the transport. For tests and for Phase 1's remote client; not used in the desktop path. */
export function setTransport(t: Transport | null): void {
  active = t;
}

/**
 * The desktop's provider relay, in one place.
 *
 * A remote client holds no provider keys and cannot reach a local server, so its requests are POSTed to
 * the desktop as an ENVELOPE describing the call it wants made: `{ url, headers, method, body }`. The
 * desktop checks the URL belongs to a configured provider, attaches the credential and makes it.
 *
 * The envelope is a wire contract with `ProviderFetchBody` in `remote.rs`. Build it HERE, never by hand:
 * `rpc.test` fails on any other module naming the route, because eight hand-written copies is what it was.
 *
 * The raw `Response` comes back because callers read it differently (bytes, text, a stream) and each
 * already has its own error wording; this owns the envelope, nothing else.
 */
export interface RelayInit {
  /** Omitted by the inference transport, which lets the desktop default it. */
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal | null;
  /** Extra envelope fields for a caller that needs one. Voice sends `auth_header`, naming WHERE its key
   *  goes (NanoGPT TTS wants a bare `x-api-key`), never the value. */
  extra?: Record<string, unknown>;
}

export function relayFetch(url: string, init: RelayInit = {}): Promise<Response> {
  return fetch("/api/provider-fetch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal: init.signal ?? null,
    body: JSON.stringify({
      url,
      headers: init.headers ?? {},
      body: init.body ?? "",
      ...(init.method ? { method: init.method } : {}),
      ...(init.extra ?? {}),
    }),
  });
}
